/**
 * Secure Auth Storage
 *
 * Provides encrypted storage for authentication data using AES-256-GCM.
 * Maintains an in-memory cache for synchronous access (required by Zustand store
 * and axios interceptors) while persisting encrypted data to localStorage.
 *
 * @module secureAuthStorage
 */

import { SecureStorage } from './security';
import { logger } from '../lib/logger';

// ============================================
// Type Definitions
// ============================================

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  roles: string[];
  permissions: string[];
  [key: string]: unknown;
}

export interface Credentials {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  access_token_expires_at: number;
  refresh_token_expires_at: number;
  [key: string]: unknown;
}

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEYS = {
  USER: 'auth_user',
  CREDENTIALS: 'auth_credentials',
  ROLES: 'auth_roles',
  PERMISSIONS: 'auth_permissions',
} as const;

// ============================================
// In-Memory Cache
// ============================================

interface AuthCache {
  user: User | null;
  credentials: Credentials | null;
  roles: string[] | null;
  permissions: string[];
  initialized: boolean;
}

const cache: AuthCache = {
  user: null,
  credentials: null,
  roles: null,
  permissions: [],
  initialized: false,
};

// ============================================
// Initialization
// ============================================

/**
 * Initialize the auth storage by loading encrypted data into cache.
 * Must be called before accessing auth data (typically in app initialization).
 *
 * @returns Promise that resolves when initialization is complete
 */
export async function initializeAuthStorage(): Promise<void> {
  if (cache.initialized) {
    return;
  }

  // Prepare values to update cache atomically
  let parsedUser: User | null = null;
  let parsedCredentials: Credentials | null = null;
  let parsedRoles: string[] | null = null;
  let parsedPermissions: string[] = [];

  try {
    // Load all auth data from encrypted storage in parallel
    const [userJson, credentialsJson, rolesJson, permissionsJson] =
      await Promise.all([
        SecureStorage.getItem(STORAGE_KEYS.USER),
        SecureStorage.getItem(STORAGE_KEYS.CREDENTIALS),
        SecureStorage.getItem(STORAGE_KEYS.ROLES),
        SecureStorage.getItem(STORAGE_KEYS.PERMISSIONS),
      ]);

    // Parse user
    if (userJson) {
      try {
        parsedUser = JSON.parse(userJson) as User;
      } catch (e) {
        logger.error('Failed to parse stored user:', e as Error);
      }
    }

    // Parse credentials
    if (credentialsJson) {
      try {
        parsedCredentials = JSON.parse(credentialsJson) as Credentials;
      } catch (e) {
        logger.error('Failed to parse stored credentials:', e as Error);
      }
    }

    // Parse roles
    if (rolesJson) {
      try {
        parsedRoles = JSON.parse(rolesJson) as string[];
      } catch (e) {
        logger.error('Failed to parse stored roles:', e as Error);
      }
    }

    // Parse permissions
    if (permissionsJson) {
      try {
        parsedPermissions = JSON.parse(permissionsJson) as string[];
      } catch (e) {
        logger.error('Failed to parse stored permissions:', e as Error);
        parsedPermissions = [];
      }
    }

    // Update cache atomically after all async operations complete
    Object.assign(cache, {
      user: parsedUser,
      credentials: parsedCredentials,
      roles: parsedRoles,
      permissions: parsedPermissions,
      initialized: true,
    });

    logger.info('Secure auth storage initialized');
  } catch (error) {
    logger.error('Failed to initialize secure auth storage:', error as Error);
    // Mark as initialized to prevent loops
    Object.assign(cache, { initialized: true });
  }
}

/**
 * Check if storage has been initialized
 */
export function isInitialized(): boolean {
  return cache.initialized;
}

// ============================================
// Synchronous Getters (from cache)
// ============================================

/**
 * Get stored user (sync, from cache)
 */
export function getStoredUser(): User | null {
  if (!cache.initialized) {
    logger.warn(
      'Auth storage not initialized. Call initializeAuthStorage() first.'
    );
  }
  return cache.user;
}

/**
 * Get stored credentials (sync, from cache)
 */
export function getStoredCredentials(): Credentials | null {
  if (!cache.initialized) {
    logger.warn(
      'Auth storage not initialized. Call initializeAuthStorage() first.'
    );
  }
  return cache.credentials;
}

/**
 * Get stored roles (sync, from cache)
 */
export function getStoredRoles(): string[] | null {
  if (!cache.initialized) {
    logger.warn(
      'Auth storage not initialized. Call initializeAuthStorage() first.'
    );
  }
  return cache.roles;
}

/**
 * Get stored permissions (sync, from cache)
 */
export function getStoredPermissions(): string[] {
  if (!cache.initialized) {
    logger.warn(
      'Auth storage not initialized. Call initializeAuthStorage() first.'
    );
  }
  return cache.permissions;
}

// ============================================
// Async Setters (encrypts and persists)
// ============================================

/**
 * Store user data (encrypted)
 */
export async function setStoredUser(user: User): Promise<void> {
  cache.user = user;
  await SecureStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
}

/**
 * Store credentials (encrypted)
 */
export async function setStoredCredentials(
  credentials: Credentials
): Promise<void> {
  cache.credentials = credentials;
  await SecureStorage.setItem(
    STORAGE_KEYS.CREDENTIALS,
    JSON.stringify(credentials)
  );
}

/**
 * Store roles (encrypted)
 */
export async function setStoredRoles(roles: string[]): Promise<void> {
  cache.roles = roles;
  await SecureStorage.setItem(STORAGE_KEYS.ROLES, JSON.stringify(roles));
}

/**
 * Store permissions (encrypted)
 */
export async function setStoredPermissions(
  permissions: string[]
): Promise<void> {
  cache.permissions = permissions;
  await SecureStorage.setItem(
    STORAGE_KEYS.PERMISSIONS,
    JSON.stringify(permissions)
  );
}

/**
 * Store all auth data at once (more efficient)
 */
export async function setAuthData(data: {
  user: User;
  credentials: Credentials;
  roles: string[];
  permissions: string[];
}): Promise<void> {
  // Update cache immediately
  cache.user = data.user;
  cache.credentials = data.credentials;
  cache.roles = data.roles;
  cache.permissions = data.permissions;

  // Persist encrypted data in parallel
  await Promise.all([
    SecureStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user)),
    SecureStorage.setItem(
      STORAGE_KEYS.CREDENTIALS,
      JSON.stringify(data.credentials)
    ),
    SecureStorage.setItem(STORAGE_KEYS.ROLES, JSON.stringify(data.roles)),
    SecureStorage.setItem(
      STORAGE_KEYS.PERMISSIONS,
      JSON.stringify(data.permissions)
    ),
  ]);
}

// ============================================
// Clear Storage
// ============================================

/**
 * Clear all auth storage (both cache and encrypted storage)
 */
export function clearAuthStorage(): void {
  // Clear cache
  cache.user = null;
  cache.credentials = null;
  cache.roles = null;
  cache.permissions = [];

  // Clear encrypted storage
  SecureStorage.removeItem(STORAGE_KEYS.USER);
  SecureStorage.removeItem(STORAGE_KEYS.CREDENTIALS);
  SecureStorage.removeItem(STORAGE_KEYS.ROLES);
  SecureStorage.removeItem(STORAGE_KEYS.PERMISSIONS);

  // Also clear legacy unencrypted storage (migration)
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_credentials');
  localStorage.removeItem('auth_roles');
  localStorage.removeItem('auth_permissions');

  // Clear Zustand persisted state
  localStorage.removeItem('auth-storage');
}

// ============================================
// Token Validation
// ============================================

/**
 * Check if access token is valid (not expired)
 */
export function isAccessTokenValid(): boolean {
  const credentials = getStoredCredentials();
  if (!credentials) return false;

  const now = Math.floor(Date.now() / 1000);
  const expiresAt = credentials.access_token_expires_at;
  const bufferTime = 60; // 60 seconds buffer

  return now < expiresAt - bufferTime;
}

/**
 * Check if refresh token is valid (not expired)
 */
export function isRefreshTokenValid(): boolean {
  const credentials = getStoredCredentials();
  if (!credentials) return false;

  const now = Math.floor(Date.now() / 1000);
  const expiresAt = credentials.refresh_token_expires_at;

  return now < expiresAt;
}

/**
 * Seconds remaining until the access token expires.
 * Returns 0 when no credentials are loaded or the token is already expired.
 */
export function getAccessTokenExpiresIn(): number {
  const credentials = getStoredCredentials();
  if (!credentials?.access_token_expires_at) return 0;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, credentials.access_token_expires_at - now);
}

/**
 * Seconds remaining until the refresh token expires (i.e. the real session-end).
 * Returns 0 when no credentials are loaded or the token is already expired.
 */
export function getRefreshTokenExpiresIn(): number {
  const credentials = getStoredCredentials();
  if (!credentials?.refresh_token_expires_at) return 0;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, credentials.refresh_token_expires_at - now);
}

// ============================================
// Migration from Unencrypted Storage
// ============================================

/**
 * Parse a Zustand persist snapshot and, when it contains a user object,
 * migrate it into encrypted SecureStorage. Returns true when a user was
 * migrated, false otherwise. Extracted to keep migrateFromUnencryptedStorage
 * shallow enough to satisfy max-depth.
 */
async function migrateZustandState(zustandState: string): Promise<boolean> {
  try {
    const parsed = JSON.parse(zustandState) as { state?: { user?: unknown } };
    if (parsed.state?.user) {
      await SecureStorage.setItem(
        STORAGE_KEYS.USER,
        JSON.stringify(parsed.state.user)
      );
      return true;
    }
  } catch (e) {
    logger.error('Failed to parse Zustand state:', e as Error);
  }
  return false;
}

/**
 * Migrate existing unencrypted localStorage data to encrypted storage.
 * Call this once during app initialization if needed.
 */
export async function migrateFromUnencryptedStorage(): Promise<boolean> {
  let migrated = false;

  // Check if there's unencrypted data that needs migration
  const unencryptedUser = localStorage.getItem('auth_user');
  const unencryptedCredentials = localStorage.getItem('auth_credentials');
  const unencryptedRoles = localStorage.getItem('auth_roles');
  const unencryptedPermissions = localStorage.getItem('auth_permissions');

  // Also check for Zustand persisted state
  const zustandState = localStorage.getItem('auth-storage');

  // Check if secure storage already has data
  const secureUser = await SecureStorage.getItem(STORAGE_KEYS.USER);

  // Only migrate if there's unencrypted data and no secure data
  if (
    (unencryptedUser || unencryptedCredentials || zustandState) &&
    !secureUser
  ) {
    logger.info('Migrating auth data to secure storage...');

    try {
      // Migrate from Zustand state if present
      if (zustandState) {
        if (await migrateZustandState(zustandState)) {
          migrated = true;
        }
        localStorage.removeItem('auth-storage');
      }

      // Migrate user
      if (unencryptedUser) {
        await SecureStorage.setItem(STORAGE_KEYS.USER, unencryptedUser);
        localStorage.removeItem('auth_user');
        migrated = true;
      }

      // Migrate credentials
      if (unencryptedCredentials) {
        await SecureStorage.setItem(
          STORAGE_KEYS.CREDENTIALS,
          unencryptedCredentials
        );
        localStorage.removeItem('auth_credentials');
        migrated = true;
      }

      // Migrate roles
      if (unencryptedRoles) {
        await SecureStorage.setItem(STORAGE_KEYS.ROLES, unencryptedRoles);
        localStorage.removeItem('auth_roles');
        migrated = true;
      }

      // Migrate permissions
      if (unencryptedPermissions) {
        await SecureStorage.setItem(
          STORAGE_KEYS.PERMISSIONS,
          unencryptedPermissions
        );
        localStorage.removeItem('auth_permissions');
        migrated = true;
      }

      if (migrated) {
        logger.info('Auth data migration to secure storage complete');
      }
    } catch (error) {
      logger.error(
        'Failed to migrate auth data to secure storage:',
        error as Error
      );
    }
  }

  return migrated;
}

// Export default object for convenience
export default {
  initialize: initializeAuthStorage,
  isInitialized,
  getStoredUser,
  getStoredCredentials,
  getStoredRoles,
  getStoredPermissions,
  setStoredUser,
  setStoredCredentials,
  setStoredRoles,
  setStoredPermissions,
  setAuthData,
  clearAuthStorage,
  isAccessTokenValid,
  isRefreshTokenValid,
  migrateFromUnencryptedStorage,
};
